import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Radio, Gamepad2, Loader2, Upload, ImageIcon, Mail, Users, CalendarDays, Video, FolderOpen, Trash2, Copy, X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import BuilderTopBar from '@/components/creation-flow/BuilderTopBar';
import { BuilderCard, builderLabelStyle, builderErrorStyle, builderHelpStyle } from '@/components/creation-flow/BuilderCard';
import { UploadProgress } from '@/components/creation-flow/UploadProgress';
import { createProduct, updateProduct, fetchProductForEdit, getProductFileUploadUrl, uploadToSignedUrl, requestMuxIngest, generateReplayChapters, saveReplayChapters, fetchHostCheck, formatClock, type CreatorProduct, type MuxPreview, type ReplayChapters, type RegistrationQuestion, type RegistrationQuestionType, type HostCheck, type ProductDownload } from '@/services/creatorApi';
import { useToast } from '@/hooks/use-toast';
import CategoryTagsFields from '@/components/creation-flow/CategoryTagsFields';
import RosterCard from '@/components/creation-flow/RosterCard';
import MakeInStudioLink from '@/components/creation-flow/MakeInStudioLink';
import { RecordingModal } from '@/components/recording/RecordingModal';
import LibraryPicker, { type StagedLibraryFile } from '@/components/library/LibraryPicker';
import VideoEditDrawer from '@/components/video-editor/VideoEditDrawer';
import { gameshowApi } from '@/services/gameshowApi';
import { viewerZoneLabel } from '@/lib/eventTime';

// ISO date → "YYYY-MM-DDTHH:mm" in the browser's local time, for datetime-local.
const toLocalDatetimeInput = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Sort + dedupe a list of datetime-local strings ascending.
const sortDates = (list: string[]) =>
  [...new Set(list)].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

// A datetime-local string → "Fri, Jul 31 · 12:00 PM".
const formatSession = (dtLocal: string) => {
  const d = new Date(dtLocal);
  if (isNaN(d.getTime())) return dtLocal;
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
};

// Add N days to a datetime-local string, preserving wall-clock time.
const addDaysToLocal = (dtLocal: string, days: number) => {
  const d = new Date(dtLocal);
  d.setDate(d.getDate() + days);
  return toLocalDatetimeInput(d.toISOString());
};

const isFutureLocal = (dtLocal: string) => new Date(dtLocal).getTime() > Date.now();

interface PrefillState {
  title?: string;
  description?: string;
  priceCents?: number;
}

type VenueDraft = { name: string; city: string; address: string; notes: string };
const emptyVenue = (): VenueDraft & { details: string } => ({ name: '', city: '', address: '', notes: '', details: '' });

// One presenter on one date: who they are, how they're billed, their face, and
// the address that actually gets them into the room that week.
type HostDraft = { name: string; role: string; image: string; email: string };
const emptyHost = (): HostDraft => ({ name: '', role: '', image: '', email: '' });

const WebinarBuilderPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isEditing = !!productId;
  const prefill = (location.state || {}) as PrefillState;

  // Game-show mode: a featured event product with showFormat:'gameshow' plus a linked
  // game_shows engine. Detected from ?format=gameshow on create, or the loaded
  // product's showFormat on edit. gameShowId links to the host panel.
  const [isGameShow, setIsGameShow] = useState(
    new URLSearchParams(location.search).get('format') === 'gameshow'
  );
  const [gameShowId, setGameShowId] = useState<string | null>(null);

  // Game shows browse under Games & Trivia — set it automatically so the
  // creator never has to think about taxonomy for a show. They can still
  // change it; we only fill it while it's empty.
  useEffect(() => {
    if (isGameShow) setCategory(prev => prev || 'games');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGameShow]);

  const [title, setTitle] = useState(prefill.title || '');
  const [description, setDescription] = useState(prefill.description || '');
  const [priceInput, setPriceInput] = useState(prefill.priceCents != null ? (prefill.priceCents / 100).toFixed(2) : '15.00');
  // Multi-session scheduling: a sorted list of datetime-local strings (all
  // occurrences), plus the draft input + repeat controls used to add them.
  const [eventDates, setEventDates] = useState<string[]>([]);
  // "Dates to be announced" — for a real event whose dates genuinely don't exist
  // yet (a club that follows whatever festival is on). Explicit, because a
  // silently dateless live page is the same broken page as a made-up date.
  const [datesTBA, setDatesTBA] = useState(false);
  const [dateWatchers, setDateWatchers] = useState<number | null>(null);
  const [dateDraft, setDateDraft] = useState('');
  const [repeat, setRepeat] = useState<'none' | 'weekly' | 'biweekly'>('none');
  const [repeatCount, setRepeatCount] = useState(6);
  const [joinUrl, setJoinUrl] = useState('');
  // Where the event happens. 'heyjoe' = a Hey Joe event room casting makes for
  // this product on save (presenters on camera, everyone else on the stream);
  // 'own' = a pasted Zoom/Meet/etc. link. New events default to the room.
  // Casting has not shipped room provisioning yet (docs/casting-ticket-event-room-provision.md),
  // so the room option only shows on a product that already has one. Flip this when it lands.
  const EVENT_ROOMS_AVAILABLE = true;
  const [roomMode, setRoomMode] = useState<'heyjoe' | 'own'>(EVENT_ROOMS_AVAILABLE ? 'heyjoe' : 'own');
  // Who's in the meeting. Default keeps the audience on the stream; 'meeting'
  // drops everyone into the room itself (the Waiting Room shape), capped at 150.
  const [audienceMode, setAudienceMode] = useState<'stream' | 'meeting'>('stream');
  const [eventRoom, setEventRoom] = useState<{ hostUrl?: string | null; syncedAt?: string } | null>(null);
  const [duration, setDuration] = useState('');
  // Presenter shown on the page and in every registrant email. Blank hostRole
  // falls back to 'Host' for featured events at render time.
  const [hostName, setHostName] = useState('');
  const [hostRole, setHostRole] = useState('');
  // Presenter addresses, comma-separated in the field, an array on the wire. Not
  // public: this is what routes someone into the room on camera and sends the
  // one-hour host reminder.
  const [hostEmails, setHostEmails] = useState('');
  // Who shares the creator payout (the money left after Hey Joe's fee), as a
  // percentage of it. Whatever is not assigned stays with the creator of record.
  const [payees, setPayees] = useState<{ email: string; name: string; percent: string }[]>([]);
  // Other stores that own this event too. It shows on their stores and they can
  // run it; saving gives each new one an even share of the payout below.
  const [coOwners, setCoOwners] = useState<{ slug: string; displayName: string }[]>([]);
  const [coOwnerInput, setCoOwnerInput] = useState('');
  const [coOwnerBusy, setCoOwnerBusy] = useState(false);
  // False for a co-owner store: they edit the event, not who owns it or who gets paid.
  const [canSetPayouts, setCanSetPayouts] = useState(true);
  // After the event: one email to people who came, one to people who didn't.
  // Off until the host switches it on; blank fields fall back to Hey Joe's defaults.
  const [followupOn, setFollowupOn] = useState(true);
  const [followupAttended, setFollowupAttended] = useState({ subject: '', body: '' });
  const [followupMissed, setFollowupMissed] = useState({ subject: '', body: '' });
  // Account status per presenter address, from the server (saved values only).
  const [hostCheck, setHostCheck] = useState<HostCheck[]>([]);
  const refreshHostCheck = (id?: string) => {
    const pid = id || productId;
    if (!pid) return;
    fetchHostCheck(pid).then(setHostCheck).catch(() => setHostCheck([]));
  };
  // What casting needs to create the account, ready to paste into a message.
  const castingRequest = (email: string) => [
    `Please create a Hey Joe account for a presenter.`,
    ``,
    `Name:  ${hostName.trim() || '(name not set)'}`,
    `Email: ${email}`,
    `Event: ${title.trim() || '(untitled)'}`,
    `When:  ${eventDates.length ? formatSession(sortDates(eventDates)[0]) : '(no date set)'}`,
    `Room:  ${joinUrl.trim() || '(Hey Joe event room)'}`,
    ``,
    `They present on camera, so they need to be signed in when they join.`,
    `Session manager is fine. Send them the sign-in details directly.`,
  ].join('\n');
  // Per-date guest overrides. A recurring featured event has a different guest each
  // week; the Host field above is the default for any date without one.
  // Per-date presenters, as PEOPLE. A date can have two — Erica Worzel and Faith
  // Olweny run the Sep 18 Waiting Room — and each one carries their own billing,
  // headshot and room access. The single-line credit the emails and the .ics read
  // is derived server-side from this list.
  const [occHosts, setOccHosts] = useState<Record<string, HostDraft[]>>({});
  const [uploadingHostPhoto, setUploadingHostPhoto] = useState<string | null>(null);
  // Online, in person, or both. 'online' keeps the room picker exactly as it was,
  // so every event built before this is unchanged.
  const [locationMode, setLocationMode] = useState<'online' | 'in_person' | 'both'>('online');
  const [venue, setVenue] = useState<VenueDraft>({ name: '', city: '', address: '', notes: '' });
  // Per-date venue + what's on that week. A recurring event can move and change
  // subject week to week, so both have to be settable per occurrence.
  const [occDetails, setOccDetails] = useState<Record<string, VenueDraft & { details: string }>>({});
  // Published, but the creator has no storefront, so it will not appear in the
  // marketplace. Set by the save response.
  const [storeMissing, setStoreMissing] = useState(false);
  // Which halves of "where it happens" are in play. Online covers the legacy
  // default, so nothing built before this changes behaviour.
  const hasOnline = locationMode !== 'in_person';
  const hasVenue = locationMode === 'in_person' || locationMode === 'both';
  const [coverImage, setCoverImage] = useState('');
  // Files that come with registering (a slide deck, a worksheet). Stored on the
  // product as keys in the product bucket; registrants and replay viewers get
  // signed links. PDFs and images only, 25 MB each, up to 10.
  const [downloads, setDownloads] = useState<ProductDownload[]>([]);
  const [uploadingDownload, setUploadingDownload] = useState(false);
  const handleDownloadUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!ok) { toast({ title: 'PDFs and images only', variant: 'destructive' }); e.target.value = ''; return; }
    if (file.size > 25 * 1024 * 1024) { toast({ title: 'Keep each file under 25 MB', variant: 'destructive' }); e.target.value = ''; return; }
    setUploadingDownload(true);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`download-${file.name}`, file.type);
      await uploadToSignedUrl(uploadUrl, file, () => {});
      const title = file.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim() || 'Download';
      setDownloads((list) => [...list, { id: Math.random().toString(36).slice(2, 12), title, s3Key: key, bytes: file.size }]);
      toast({ title: 'Added — save to finalize' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingDownload(false); e.target.value = ''; }
  };
  const [published, setPublished] = useState(true);
  const [recordingIncluded, setRecordingIncluded] = useState(false); // opt-in per event; legacy events load as included
  // Active replay: the host promises to answer replay questions within 24 hours
  // for N days after the last session. Off unless the creator switches it on.
  const [activeReplayOn, setActiveReplayOn] = useState(false);
  const [activeReplayDays, setActiveReplayDays] = useState('7');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [vanityCode, setVanityCode] = useState('');
  // Ticket types (optional): e.g. "Work with me" (capped participant seats) vs
  // "Observer". When set, buyers pick one and its price/cap is enforced at
  // checkout; the base price above becomes a fallback display only.
  // memberPriceCents / annualMemberPriceCents / plusOfferMonths are carried
  // through untouched. This editor doesn't expose them (they're set in the admin
  // member-pricing panel), but the backend sanitiser rebuilds each tier from what
  // the client sends — so a tier saved without them loses them. That is exactly
  // how the Aug 22 class stopped being free for members: a seat-cap edit on Aug 7
  // re-saved the tiers and silently dropped the member prices, while two waves of
  // email kept promising members it was free.
  const [tierDrafts, setTierDrafts] = useState<{
    key?: string; label: string; price: string; seatCap: string; description: string;
    memberPriceCents?: number; annualMemberPriceCents?: number; plusOfferMonths?: number | null;
  }[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Intro video — a short pitch clip shown on the public class page. Same
  // "Preview clip" concept video products have: a public Mux asset provisioned
  // on save via requestMuxIngest({ kind: 'preview' }). NOT the event recording.
  const [previewClipS3Key, setPreviewClipS3Key] = useState<string | null>(null);
  const [previewClipName, setPreviewClipName] = useState<string | null>(null);
  const [uploadingPreviewClip, setUploadingPreviewClip] = useState(false);
  // Upload progress 0-100, -1 when the browser can't report a total, null when idle.
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [existingMuxPreview, setExistingMuxPreview] = useState<MuxPreview | null>(null);
  // Questions asked at registration (and with early-access requests on agency-only
  // events). Same field model as casting's custom questions builder, minus uploads.
  const [questions, setQuestions] = useState<RegistrationQuestion[]>([]);
  const Q_TYPES: { value: RegistrationQuestionType; label: string; hasOptions: boolean }[] = [
    { value: 'checkbox', label: 'Multiple choice (pick any)', hasOptions: true },
    { value: 'radio', label: 'Single choice', hasOptions: true },
    { value: 'select', label: 'Dropdown', hasOptions: true },
    { value: 'yesno', label: 'Yes / No', hasOptions: false },
    { value: 'text', label: 'Short text', hasOptions: false },
    { value: 'textarea', label: 'Long text', hasOptions: false },
  ];
  const addQuestion = () => setQuestions(qs => [...qs, { id: `q${Date.now().toString(36)}`, label: '', type: 'checkbox', options: [''], required: false }]);
  const patchQuestion = (i: number, patch: Partial<RegistrationQuestion>) => setQuestions(qs => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const removeQuestion = (i: number) => setQuestions(qs => qs.filter((_, j) => j !== i));
  // Replay chapters: generated from the recording after the event; titles and
  // summaries are editable here and save on their own (not part of the form).
  const [replayChapters, setReplayChapters] = useState<ReplayChapters | null>(null);
  const [chapterDraft, setChapterDraft] = useState<{ title: string; summary: string; startSec: number }[]>([]);
  const [chapterOverview, setChapterOverview] = useState('');
  const [chaptersBusy, setChaptersBusy] = useState<'generate' | 'save' | null>(null);
  const [chaptersDirty, setChaptersDirty] = useState(false);
  const loadChapters = (rc: ReplayChapters | null | undefined) => {
    setReplayChapters(rc || null);
    setChapterDraft((rc?.chapters || []).map(c => ({ title: c.title, summary: c.summary || '', startSec: c.startSec })));
    setChapterOverview(rc?.overview || '');
    setChaptersDirty(false);
  };
  const regenerateChapters = async () => {
    if (!productId) return;
    setChaptersBusy('generate');
    try {
      const out = await generateReplayChapters(productId);
      if (out.chapters) { loadChapters(out.chapters); toast({ title: `${out.chapters.chapters.length} chapters generated` }); }
      else toast({ title: 'No chapters yet', description: out.message || out.status });
    } catch (err: any) { toast({ title: 'Could not generate chapters', description: err.message, variant: 'destructive' }); }
    finally { setChaptersBusy(null); }
  };
  const saveChapters = async () => {
    if (!productId) return;
    setChaptersBusy('save');
    try {
      const saved = await saveReplayChapters(productId, { overview: chapterOverview, chapters: chapterDraft.filter(c => c.title.trim()) });
      loadChapters(saved);
      toast({ title: 'Chapters saved' });
    } catch (err: any) { toast({ title: 'Could not save chapters', description: err.message, variant: 'destructive' }); }
    finally { setChaptersBusy(null); }
  };
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [editingPreview, setEditingPreview] = useState(false);

  const [dirty, setDirty] = useState(false);
  const initialLoadRef = useRef(true);

  // Load existing featured event
  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetchProductForEdit(productId)
      .then(p => {
        if (!p) { toast({ title: 'Listing not found', variant: 'destructive' }); navigate('/my-store'); return; }
        setTitle(p.title);
        setDescription(p.description);
        setPriceInput((p.price / 100).toFixed(2));
        setCoverImage(p.coverImage || '');
        setDownloads(Array.isArray(p.downloads) ? p.downloads : []);
        setPublished(p.published);
        const loadedDates = Array.isArray(p.eventDates) && p.eventDates.length
          ? p.eventDates
          : (p.eventDate ? [p.eventDate] : []);
        setEventDates(sortDates(loadedDates.map(toLocalDatetimeInput)));
        setJoinUrl((p as any).joinUrl || '');
        setRoomMode((p as any).joinProvider === 'heyjoe' && ((p as any).eventRoom?.sessionId || EVENT_ROOMS_AVAILABLE) ? 'heyjoe' : 'own');
        setEventRoom((p as any).eventRoom || null);
        setRecordingIncluded((p as any).recordingIncluded !== false);
        setActiveReplayOn(!!p.activeReplay?.enabled);
        setActiveReplayDays(String(p.activeReplay?.days || 7));
        setCategory((p as any).category || '');
        setTags((p as any).tags || []);
        setVanityCode((p as any).vanityCode || '');
        setDuration(p.durationMins ? String(p.durationMins) : '');
        setHostName((p as any).instructor || '');
        setHostRole((p as any).hostRole || '');
        setHostEmails((Array.isArray((p as any).hostEmails) ? (p as any).hostEmails : ((p as any).hostEmail ? [(p as any).hostEmail] : [])).join(', '));
        setPayees((p.payouts || []).map(x => ({ email: x.email, name: x.name || '', percent: String(x.percent) })));
        setCoOwners((p.coOwners || []).map(c => ({ slug: c.slug, displayName: c.displayName })));
        setCanSetPayouts(p.canSetPayouts !== false);
        refreshHostCheck(p._id);
        fetch(`/api/creator/products/${p._id}/date-watchers`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || localStorage.getItem('token') || ''}` } })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setDateWatchers(d.count); })
          .catch(() => { /* the count is a nicety, never a blocker */ });
        const oh: Record<string, HostDraft[]> = {};
        for (const o of ((p as any).occurrenceHosts || [])) {
          if (!o?.eventDate) continue;
          const key = new Date(o.eventDate).toISOString();
          if (Array.isArray(o.hosts) && o.hosts.length) {
            oh[key] = o.hosts.map((h: any) => ({
              name: h.name || '', role: h.role || '', image: h.image || '', email: h.email || '',
            }));
            continue;
          }
          // A row written before this field held people: one free-text credit for
          // the week, which is where two hosts got crammed into one string
          // ("Erica Worzel / Faith Olweny"). Split it back into rows so the week
          // opens editable instead of needing to be retyped. Only on the
          // separators that mean "and another person" — never on a comma, which
          // is how a credit is attached ("Hunter McHugh, CSA").
          const names = String(o.instructor || '').split(/\s*[/&]\s*/).map((n: string) => n.trim()).filter(Boolean);
          const mails: string[] = Array.isArray(o.emails) ? o.emails : [];
          oh[key] = (names.length ? names : ['']).map((n: string, i: number) => ({
            name: n,
            role: o.hostRole || '',
            image: '',
            // Addresses were entered in the same order as the names, so they line
            // up — but only claim that when the counts actually match. Otherwise
            // they all ride on the first row rather than being handed to the
            // wrong person.
            email: names.length === mails.length ? (mails[i] || '') : (i === 0 ? mails.join(', ') : ''),
          })).filter((h: HostDraft) => h.name || h.email);
          if (!oh[key].length) delete oh[key];
        }
        setOccHosts(oh);
        setDatesTBA(!!(p as any).datesTBA);
        setLocationMode(((p as any).locationMode as any) || 'online');
        const lv = (p as any).venue;
        setVenue({ name: lv?.name || '', city: lv?.city || '', address: lv?.address || '', notes: lv?.notes || '' });
        const od: Record<string, VenueDraft & { details: string }> = {};
        for (const o of ((p as any).occurrenceDetails || [])) {
          if (o?.eventDate) od[new Date(o.eventDate).toISOString()] = {
            name: o.name || '', city: o.city || '', address: o.address || '', notes: o.notes || '', details: o.details || '',
          };
        }
        setOccDetails(od);
        setTierDrafts(((p as any).ticketTiers || []).map((t: any) => ({
          key: t.key,
          label: t.label || '',
          price: ((t.priceCents ?? 0) / 100).toFixed(2),
          seatCap: t.seatCap ? String(t.seatCap) : '',
          description: t.description || '',
          // Not editable here — preserved so saving this form can't erase them.
          memberPriceCents: t.memberPriceCents,
          annualMemberPriceCents: t.annualMemberPriceCents,
          plusOfferMonths: t.plusOfferMonths,
        })));
        setExistingMuxPreview(p.muxPreview || null);
        loadChapters(p.replayChapters);
        setQuestions(Array.isArray(p.registrationQuestions) ? p.registrationQuestions : []);
        setFollowupOn(p.followupEmails?.enabled !== false);
        setAudienceMode(p.audienceMode === 'meeting' ? 'meeting' : 'stream');
        setFollowupAttended({ subject: p.followupEmails?.attended?.subject || '', body: p.followupEmails?.attended?.body || '' });
        setFollowupMissed({ subject: p.followupEmails?.missed?.subject || '', body: p.followupEmails?.missed?.body || '' });
        if ((p as any).showFormat === 'gameshow') {
          setIsGameShow(true);
          gameshowApi.forProduct(p._id).then(r => setGameShowId(r.show._id)).catch(() => {});
        }
      })
      .catch(err => toast({ title: 'Error', description: err.message, variant: 'destructive' }))
      .finally(() => { setLoading(false); initialLoadRef.current = true; setDirty(false); });
  }, [productId]);

  // Dirty tracking (skips the load-induced render).
  useEffect(() => {
    if (initialLoadRef.current) { initialLoadRef.current = false; return; }
    setDirty(true);
  }, [title, description, priceInput, eventDates, datesTBA, joinUrl, duration, hostName, hostRole, hostEmails, payees, occHosts, locationMode, venue, occDetails, coverImage, published, recordingIncluded, category, tags, previewClipS3Key, vanityCode, tierDrafts, questions, followupOn, followupAttended, followupMissed, audienceMode, downloads, activeReplayOn, activeReplayDays]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/my-store');
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Please select an image file', variant: 'destructive' }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Image must be under 5MB', variant: 'destructive' }); return; }
    setUploadingCover(true);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`cover-${file.name}`, file.type);
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error('Upload failed');
      setCoverImage(key); // bare key — renders prepend /api/s3/
      toast({ title: 'Cover image uploaded' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingCover(false); e.target.value = ''; }
  };

  // A presenter's headshot. Same staging path as the cover — signed PUT into the
  // product bucket, bare key stored, `/api/s3/` prepended at render.
  const handleHostPhotoUpload = async (dateKey: string, index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Please select an image file', variant: 'destructive' }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Headshot must be under 5MB', variant: 'destructive' }); return; }
    const slot = `${dateKey}:${index}`;
    setUploadingHostPhoto(slot);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`host-${file.name}`, file.type);
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error('Upload failed');
      setOccHosts(m => ({
        ...m,
        [dateKey]: (m[dateKey] || []).map((h, i) => (i === index ? { ...h, image: key } : h)),
      }));
    } catch (err: any) {
      toast({ title: 'Headshot upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingHostPhoto(null); e.target.value = ''; }
  };

  // --- Intro video (public preview clip) ---
  // Stage a clip file into the product bucket (shared by upload + record). The
  // Mux asset is created at save via the preview-kind ingest fan-out.
  const stagePreviewFile = async (file: File, label?: string) => {
    setUploadingPreviewClip(true);
    setUploadPercent(0);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`preview-${file.name}`, file.type);
      await uploadToSignedUrl(uploadUrl, file, setUploadPercent);
      setPreviewClipS3Key(key); setPreviewClipName(label || file.name); setExistingMuxPreview(null);
      toast({ title: 'Intro video added — save to finalize' });
    } catch (err: any) {
      toast({ title: 'Intro video upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingPreviewClip(false); setUploadPercent(null); }
  };

  const handlePreviewClipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast({ title: 'Intro video must be a video file', variant: 'destructive' }); return; }
    if (file.size > 1024 * 1024 * 1024) { toast({ title: 'Intro video too large', description: 'Max 1GB.', variant: 'destructive' }); return; }
    await stagePreviewFile(file);
    e.target.value = '';
  };

  const handlePreviewRecorded = async (file: File) => {
    setShowRecordModal(false);
    await stagePreviewFile(file, 'Recorded intro');
  };

  // Library pick — LibraryPicker stages into creator-products/ (lesson target)
  // and hands back a Mux-ingestable key + proxy URL, exactly like an upload.
  const handlePreviewLibraryPick = (f: StagedLibraryFile) => {
    setPreviewClipS3Key(f.key); setPreviewClipName(f.name); setExistingMuxPreview(null);
    toast({ title: 'Intro video selected — save to finalize' });
  };

  const removePreviewClip = () => {
    setPreviewClipS3Key(null); setPreviewClipName(null); setExistingMuxPreview(null);
  };

  // Drawer apply: swap in the edited clip and clear the live preview so the
  // save-time ingest re-runs against the new key.
  const applyPreviewEdit = (patch: { s3Key: string }) => {
    setPreviewClipS3Key(patch.s3Key);
    setPreviewClipName(prev => prev || 'Edited intro');
    setExistingMuxPreview(null);
    setEditingPreview(false);
  };

  const previewEditKey = previewClipS3Key || existingMuxPreview?.sourceS3Key || null;

  // Add the draft date — plus its weekly/biweekly repeats when set — into the
  // scheduled list, merged/deduped/sorted. History (past dates) is left in place.
  const addSessions = () => {
    if (!dateDraft) { setErrors(x => ({ ...x, eventDates: 'Pick a date & time to add' })); return; }
    const step = repeat === 'weekly' ? 7 : repeat === 'biweekly' ? 14 : 0;
    const n = repeat === 'none' ? 1 : Math.max(2, Math.min(12, repeatCount));
    const generated: string[] = [];
    for (let i = 0; i < n; i++) generated.push(i === 0 ? dateDraft : addDaysToLocal(dateDraft, step * i));
    setEventDates(prev => sortDates([...prev, ...generated]));
    setDateDraft('');
    setErrors(x => ({ ...x, eventDates: '' }));
  };

  const removeSession = (dt: string) => {
    setEventDates(prev => prev.filter(d => d !== dt));
  };

  const handleSave = async () => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Title is required';
    const priceCents = Math.round(parseFloat(priceInput) * 100);
    if (isNaN(priceCents) || priceCents < 0 || (priceCents > 0 && priceCents < 100)) errs.price = 'Enter $0 for free, or at least $1.00';
    // Fold a picked-but-not-yet-"Added" draft date into the schedule so "pick a
    // date and hit Save" just works, without forcing the separate "Add" click
    // (a common trap — you pick a date, hit Save, and it says none was added).
    const pendingDraft = dateDraft && !eventDates.includes(dateDraft) ? [dateDraft] : [];
    const allEventDates = sortDates([...eventDates, ...pendingDraft]);
    const futureDates = allEventDates.filter(isFutureLocal);
    // A DRAFT saves with whatever it has. A date and a join link are what
    // publishing needs, not what saving needs: the Sep 8 lineup drafts were
    // written before the Zoom links existed, and "Save changes" silently did
    // nothing because the join-link error rendered three screens down.
    // A published event whose dates have all passed is a finished event, not a
    // broken one: its replay, roster and payouts still need editing. Only an
    // event with no dates at all has to pick one or say they're coming.
    if (published && !allEventDates.length && !datesTBA) errs.eventDates = 'Add a session before publishing, or mark the dates as to be announced';
    // Join link is OPTIONAL for game shows (the host drives it from the show
    // panel, not a Jitsi join) — required to PUBLISH a regular featured event. Validate
    // the format whenever one is provided.
    const jl = joinUrl.trim();
    if (jl && !/^https:\/\//i.test(jl)) errs.joinUrl = 'Join link must be an https URL';
    else if (!jl && !isGameShow && published && hasOnline && roomMode === 'own') errs.joinUrl = 'Add an https join link before publishing';
    if (published && hasVenue && !venue.address.trim()) errs.venueAddress = 'Add the street address before publishing';
    // Ticket types: rows without a label are ignored; labeled rows need a valid price.
    const ticketTiers = [];
    for (const t of tierDrafts) {
      const label = t.label.trim();
      if (!label) continue;
      const cents = Math.round(parseFloat(t.price) * 100);
      if (isNaN(cents) || cents < 0 || (cents > 0 && cents < 100)) { errs.tiers = `"${label}" needs a price of $0 (free) or at least $1.00`; break; }
      const cap = t.seatCap.trim() ? parseInt(t.seatCap, 10) : null;
      if (cap !== null && (isNaN(cap) || cap < 1)) { errs.tiers = `"${label}" has an invalid seat cap`; break; }
      ticketTiers.push({
        ...(t.key ? { key: t.key } : {}),
        label,
        priceCents: cents,
        ...(cap ? { seatCap: cap } : {}),
        ...(t.description.trim() ? { description: t.description.trim() } : {}),
        // Echo back what this form doesn't edit, or the save wipes it.
        ...(t.memberPriceCents !== undefined && t.memberPriceCents !== null ? { memberPriceCents: t.memberPriceCents } : {}),
        ...(t.annualMemberPriceCents !== undefined && t.annualMemberPriceCents !== null ? { annualMemberPriceCents: t.annualMemberPriceCents } : {}),
        ...(t.plusOfferMonths !== undefined && t.plusOfferMonths !== null ? { plusOfferMonths: t.plusOfferMonths } : {}),
      });
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      // Say so where the button is, and go to the first problem.
      toast({ title: "Can't save yet", description: Object.values(errs).join(' · '), variant: 'destructive' });
      const first = document.querySelector('[data-builder-error]');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Reflect the auto-committed draft date in the UI list too.
    if (pendingDraft.length) { setEventDates(allEventDates); setDateDraft(''); }

    // Fold a picked-but-not-added draft date into the committed schedule so the
    // list UI reflects what we're about to save.
    if (pendingDraft.length) { setEventDates(allEventDates); setDateDraft(''); }

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        title: title.trim(),
        description: description.trim(),
        price: priceCents,
        productType: 'webinar',
        pricingMode: 'one_time',
        coverImage: coverImage || undefined,
        downloads: downloads.map(({ id, title, s3Key, bytes }) => ({ id, title, s3Key, bytes })),
        published,
        videos: [],
        // All occurrences (ISO); eventDate mirrors the earliest FUTURE one for
        // every single-date surface (reminders, ICS, marketplace, emails).
        eventDates: allEventDates.map(d => new Date(d).toISOString()),
        // A real future date retires the flag server-side too; sending it honestly
        // keeps the two from disagreeing on a save that adds one.
        datesTBA: datesTBA && !futureDates.length,
        // No future date: send null and let the server derive eventDate from
        // eventDates (it falls back to the last one). new Date(undefined) here
        // used to throw and kill every save on a finished event.
        eventDate: futureDates.length ? new Date(sortDates(futureDates)[0]).toISOString() : null,
        // Room mode: the server fills joinUrl from casting; own link: send what was pasted.
        joinUrl: (!isGameShow && hasOnline && roomMode === 'heyjoe') ? '' : (hasOnline ? joinUrl.trim() : ''),
        joinProvider: (!isGameShow && hasOnline && roomMode === 'heyjoe') ? 'heyjoe' : 'other',
        audienceMode: (!isGameShow && hasOnline && roomMode === 'heyjoe') ? audienceMode : 'stream',
        durationMins: duration ? parseInt(duration, 10) : null,
        instructor: hostName.trim() || null,
        // Only questions with a label survive; choice types need at least one option.
        registrationQuestions: questions
          .map(q => ({ ...q, label: q.label.trim(), options: (q.options || []).map(o => o.trim()).filter(Boolean) }))
          .filter(q => q.label && (!['checkbox', 'radio', 'select'].includes(q.type) || (q.options || []).length > 0)),
        hostRole: hostRole.trim() || null,
        hostEmails: hostEmails.split(/[,\s]+/).map(s => s.trim()).filter(s => s.includes('@')),
        payouts: payees
          .map(x => ({ email: x.email.trim().toLowerCase(), name: x.name.trim() || null, percent: parseFloat(x.percent) || 0 }))
          .filter(x => x.email.includes('@') && x.percent > 0),
        ...(canSetPayouts ? { coOwners: coOwners.map(c => c.slug) } : {}),
        followupEmails: {
          enabled: followupOn,
          attended: followupAttended.subject.trim() && followupAttended.body.trim() ? { subject: followupAttended.subject.trim(), body: followupAttended.body.trim() } : null,
          missed: followupMissed.subject.trim() && followupMissed.body.trim() ? { subject: followupMissed.subject.trim(), body: followupMissed.body.trim() } : null,
        },
        // The server derives the one-line credit and the room's guest list from
        // `hosts`, so this sends people and nothing else. A date whose rows are all
        // blank drops out, which is how a week's hosts get cleared.
        occurrenceHosts: Object.entries(occHosts)
          .map(([eventDate, hosts]) => ({
            eventDate,
            hosts: hosts
              .filter(h => h.name.trim())
              .map(h => ({
                name: h.name.trim(),
                role: h.role.trim() || null,
                image: h.image || null,
                email: h.email.trim().toLowerCase() || null,
              })),
          }))
          .filter(r => r.hosts.length),
        locationMode,
        venue: locationMode === 'online' ? null : {
          name: venue.name.trim() || null,
          city: venue.city.trim() || null,
          address: venue.address.trim() || null,
          notes: venue.notes.trim() || null,
        },
        // Always sent — dropping a date's entry clears it.
        occurrenceDetails: Object.entries(occDetails)
          .filter(([, v]) => v.name.trim() || v.city.trim() || v.address.trim() || v.notes.trim() || v.details.trim())
          .map(([eventDate, v]) => ({
            eventDate,
            name: v.name.trim() || null,
            city: v.city.trim() || null,
            address: v.address.trim() || null,
            notes: v.notes.trim() || null,
            details: v.details.trim() || null,
          })),
        recordingIncluded,
        activeReplay: { enabled: activeReplayOn && recordingIncluded, days: Math.min(30, Math.max(1, parseInt(activeReplayDays, 10) || 7)) },
        category: category || null,
        tags,
        vanityCode: vanityCode.trim() || null,
        // Always sent — an empty array clears previously saved ticket types.
        ticketTiers,
        // Flags this featured event as a game show → backend links a game_shows engine.
        ...(isGameShow ? { showFormat: 'gameshow' } : {}),
      };
      let saved = isEditing
        ? await updateProduct(productId!, payload)
        : await createProduct(payload);
      // Create does not take co-owners; attach them to the new event straight away.
      if (!isEditing && saved?._id && canSetPayouts && coOwners.length) {
        saved = await updateProduct(saved._id, { coOwners: coOwners.map(c => c.slug) } as any);
      }

      // Intro-video Mux fan-out — provision a public preview asset when a new
      // clip was staged and it isn't the one already on the product. muxPreview
      // is written server-side by the ingest endpoint (not the product save).
      // Re-run the presenter account check against what was just stored.
      if (saved && saved._id) refreshHostCheck(saved._id);
      // The server fills in a new co-owner's default share; show what it saved.
      if (saved && Array.isArray(saved.payouts)) setPayees(saved.payouts.map(x => ({ email: x.email, name: x.name || '', percent: String(x.percent) })));
      else if (saved) setPayees([]);
      if (saved) setCoOwners((saved.coOwners || []).map(c => ({ slug: c.slug, displayName: c.displayName })));
      if (saved && saved._id && previewClipS3Key &&
          (!saved.muxPreview || saved.muxPreview.sourceS3Key !== previewClipS3Key)) {
        await Promise.race([
          requestMuxIngest({ productId: saved._id, s3Key: previewClipS3Key, kind: 'preview' })
            .catch(err => console.warn('[mux] preview ingest failed', err)),
          new Promise(resolve => setTimeout(resolve, 4000)),
        ]);
      }

      // Resolve the linked show so the host-panel button lights up (backend
      // auto-creates on create, lazily on update).
      if (isGameShow && saved?._id) {
        gameshowApi.forProduct(saved._id).then(r => setGameShowId(r.show._id)).catch(() => {});
      }

      // The room link comes back on the saved product; show it, and say so if
      // casting could not make one (the server keeps the event as a draft then).
      if (!isGameShow && roomMode === 'heyjoe') {
        if ((saved as any).joinUrl) setJoinUrl((saved as any).joinUrl);
        setEventRoom((saved as any).eventRoom || null);
        if ((saved as any).eventRoomError) {
          setPublished(!!saved.published);
          toast({ title: 'No event room yet', description: (saved as any).eventRoomError, variant: 'destructive' });
        }
      }
      if ((saved as any).payoutNotConnected) {
        toast({ title: 'Live, but not paid yet', description: 'Sales are held for you until you connect Stripe under My store. Do that before the event so the money reaches you.' });
      }
      // Published with no storefront means published and unfindable — the
      // marketplace lists stores, and a listing with no store behind it has
      // nowhere to appear. Persisted, not a toast: this one has to survive being
      // missed.
      setStoreMissing(!!(saved as any).storeMissing);
      setDirty(false);
      if (saved.reviewStatus === 'pending') {
        // First live event from this creator — held for review before going live.
        toast({ title: 'Submitted for review', description: 'Your first live event goes live once our team approves it — we’ll email you. Future ones publish right away.' });
        if (!isEditing) navigate(`/my-store/webinar/${saved._id}`, { replace: true });
      } else if (isEditing) {
        toast({ title: isGameShow ? 'Game show updated' : 'Featured event updated' });
      } else {
        toast({ title: isGameShow ? 'Game show created' : 'Featured event created' });
        navigate(`/my-store/webinar/${saved._id}`, { replace: true });
      }
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  const coverSrc = coverImage
    ? (coverImage.startsWith('http') || coverImage.startsWith('/api/') ? coverImage : `/api/s3/${coverImage}`)
    : null;

  return (
    <main className="min-h-screen" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <BuilderTopBar
        kicker={isGameShow ? (isEditing ? 'Edit game show' : 'New game show') : (isEditing ? 'Edit featured event' : 'New live featured event')}
        kickerIcon={isGameShow ? Gamepad2 : Radio}
        kickerColor="var(--hj-pink)"
        title={title}
        titlePlaceholder={isGameShow ? 'Untitled game show' : 'Untitled featured event'}
        onBack={handleBack}
        saving={saving}
        dirty={dirty}
        saveLabel={isEditing ? 'Save changes' : (isGameShow ? 'Publish game show' : 'Publish featured event')}
        onSave={handleSave}
        previewHref={isEditing ? `/course/${productId}` : undefined}
      />

      <div className="mx-auto" style={{ maxWidth: 1120, padding: '24px 16px 96px' }}>
        {storeMissing && (
          <div
            style={{
              marginBottom: 18, padding: '16px 18px', borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-beige)', border: '1.5px solid var(--hj-beige-ink)',
              color: 'var(--hj-beige-ink)', display: 'grid', gap: 8,
            }}
          >
            <p className="m-0" style={{ fontWeight: 800, fontSize: 15.5 }}>
              This is live, but it won&rsquo;t show in the marketplace yet.
            </p>
            <p className="m-0" style={{ fontSize: 14.5, lineHeight: 1.5 }}>
              The marketplace lists stores, and you don&rsquo;t have one yet — so there&rsquo;s nowhere
              for this listing to appear. Anyone with the direct link can still open it and register.
              Name your store and it shows up straight away.
            </p>
            <a
              href="/my-store#storefront"
              style={{
                justifySelf: 'start', marginTop: 2, minHeight: 40, display: 'inline-flex', alignItems: 'center',
                padding: '0 18px', borderRadius: 999, background: 'var(--hj-beige-ink)', color: 'var(--hj-beige)',
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
              }}
            >
              Set up my store →
            </a>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          {/* Left: form */}
          <div className="flex-1 min-w-0 flex flex-col gap-5">
            <BuilderCard kicker="The basics">
              <div>
                <Label style={builderLabelStyle}>Title</Label>
                <Input value={title} onChange={e => { setTitle(e.target.value); setErrors(x => ({ ...x, title: '' })); }} placeholder="e.g. Booking the Room — Live Q&A" />
                {errors.title && <p data-builder-error style={builderErrorStyle}>{errors.title}</p>}
              </div>
              <div>
                <Label style={builderLabelStyle}>Description</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder={isGameShow ? 'What is the show? Format, vibe, prizes — why should people come play?' : 'What will registrants get out of this session?'} />
              </div>
              <CategoryTagsFields category={category} setCategory={setCategory} tags={tags} setTags={setTags} />
              <div>
                <Label style={builderLabelStyle}>Vanity URL</Label>
                <div className="flex items-center gap-0">
                  <span className="mono-label flex-shrink-0" style={{ padding: '9px 10px', borderRadius: '8px 0 0 8px', border: '1px solid var(--hj-beige-line)', borderRight: 0, background: 'var(--hj-beige-hover)', color: 'var(--hj-beige-ink-3)' }}>plus.heyjoe.io/w/</span>
                  <Input
                    value={vanityCode}
                    onChange={e => setVanityCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40))}
                    placeholder="self-tape-mastery"
                    style={{ borderRadius: '0 8px 8px 0' }}
                  />
                  {vanityCode.length >= 3 && (
                    <button
                      type="button"
                      title="Copy link"
                      onClick={() => { try { navigator.clipboard.writeText(`https://plus.heyjoe.io/w/${vanityCode}`); toast({ title: 'Link copied' }); } catch { /* noop */ } }}
                      className="inline-flex items-center justify-center flex-shrink-0"
                      style={{ marginLeft: 8, width: 34, height: 34, borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: 'transparent', color: 'var(--hj-beige-ink-2)', cursor: 'pointer' }}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <p style={builderHelpStyle}>{`A clean, printable link for socials and slides — saves with the ${isGameShow ? 'show' : 'webinar'}. 3-40 characters.`}</p>
              </div>
            </BuilderCard>

            {/* Intro video — a short pitch shown on the public class page. */}
            <BuilderCard kicker="Intro video">
              <p className="m-0" style={builderHelpStyle}>A short pitch shown on your listing — what the night looks like. A minute or two holds attention best, but any length up to 1GB works.</p>
              {/* Real progress while the file goes to S3. */}
              <UploadProgress percent={uploadingPreviewClip ? uploadPercent : null} />
              {(previewClipS3Key || existingMuxPreview) ? (
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2.5" style={{ padding: '8px 10px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-hover)', border: '1px solid var(--hj-beige-line)' }}>
                    <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 30, height: 30, borderRadius: 8, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)' }}><Video className="w-4 h-4" /></span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{previewClipName || 'Intro video'}</div>
                      <div style={{ fontSize: 11, color: 'var(--hj-beige-ink-3)' }}>
                        {previewClipS3Key
                          ? 'Staged — saves on submit'
                          : `Active (${existingMuxPreview?.muxIngestStatus === 'ready' ? 'live' : existingMuxPreview?.muxIngestStatus})`}
                      </div>
                    </div>
                    <button type="button" onClick={removePreviewClip} aria-label="Remove intro video" title="Remove" className="flex-shrink-0" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, minHeight: 34 }}>
                      {uploadingPreviewClip ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Replace
                      <input type="file" accept="video/*" className="hidden" onChange={handlePreviewClipUpload} disabled={uploadingPreviewClip} />
                    </label>
                    {!!previewEditKey && (
                      <button type="button" onClick={() => setEditingPreview(true)} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12, fontWeight: 600, minHeight: 34, cursor: 'pointer' }}>
                        <Video className="w-3.5 h-3.5" /> Edit video
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-1.5 w-full justify-center" style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38 }}>
                      {uploadingPreviewClip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload a clip
                    </span>
                    <input type="file" accept="video/*" className="hidden" onChange={handlePreviewClipUpload} disabled={uploadingPreviewClip} />
                  </label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowRecordModal(true)} disabled={uploadingPreviewClip} className="flex-1 inline-flex items-center justify-center gap-1.5" style={{ padding: '8px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}>
                      <Video className="w-4 h-4" /> Record
                    </button>
                    <button type="button" onClick={() => setShowLibraryPicker(true)} disabled={uploadingPreviewClip} className="flex-1 inline-flex items-center justify-center gap-1.5" style={{ padding: '8px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}>
                      <FolderOpen className="w-4 h-4" /> Library
                    </button>
                  </div>
                </div>
              )}
            </BuilderCard>

            <BuilderCard kicker="When & where">
              <div>
                <Label style={builderLabelStyle}>Sessions</Label>
                {/* Scheduled occurrences — past ones stay for history, dimmed. */}
                {eventDates.length > 0 && (
                  <div className="flex flex-col gap-1.5" style={{ marginBottom: 10 }}>
                    {eventDates.map(dt => {
                      const past = !isFutureLocal(dt);
                      return (
                        <div
                          key={dt}
                          className="flex items-center gap-2"
                          style={{ padding: '7px 10px', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-hover)', border: '1px solid var(--hj-beige-line)', opacity: past ? 0.5 : 1 }}
                        >
                          <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--hj-beige-ink-2)' }} />
                          <span className="flex-1 min-w-0 truncate" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{formatSession(dt)}</span>
                          {past && <span className="mono-label flex-shrink-0" style={{ color: 'var(--hj-beige-ink-3)' }}>past</span>}
                          <button type="button" onClick={() => removeSession(dt)} aria-label="Remove session" title="Remove" className="flex-shrink-0 inline-flex items-center justify-center" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)', width: 20, height: 20 }}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Add a session (optionally repeating). */}
                <div className="flex flex-col gap-2" style={{ padding: 10, borderRadius: 'var(--hj-r-sm)', border: '1px dashed var(--hj-beige-line)' }}>
                  <Input type="datetime-local" value={dateDraft} onChange={e => { setDateDraft(e.target.value); setErrors(x => ({ ...x, eventDates: '' })); }} />
                  {/* The browser reads this in the creator's own zone; the page then
                      shows every viewer the time in theirs. Say so, or a creator in
                      Texas types the Pacific time "to be safe" and is off by two hours. */}
                  <p className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-2)' }}>
                    Enter the start in your time zone, {viewerZoneLabel()}. Everyone sees it in theirs.
                  </p>
                  <div className="flex items-center gap-2">
                    <select
                      value={repeat}
                      onChange={e => setRepeat(e.target.value as 'none' | 'weekly' | 'biweekly')}
                      className="flex-1"
                      style={{ padding: '9px 10px', borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5 }}
                    >
                      <option value="none">Just once</option>
                      <option value="weekly">Every week</option>
                      <option value="biweekly">Every 2 weeks</option>
                    </select>
                    {repeat !== 'none' && (
                      <Input
                        type="number"
                        min={2}
                        max={12}
                        value={repeatCount}
                        onChange={e => setRepeatCount(Math.max(2, Math.min(12, parseInt(e.target.value, 10) || 2)))}
                        style={{ width: 68 }}
                        aria-label="Number of sessions"
                      />
                    )}
                    <button
                      type="button"
                      onClick={addSessions}
                      className="inline-flex items-center justify-center flex-shrink-0"
                      style={{ padding: '9px 16px', borderRadius: 999, background: 'var(--hj-beige-ink)', color: '#fff', border: 0, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 38 }}
                    >
                      Add
                    </button>
                  </div>
                  {repeat !== 'none' && (
                    <p className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>Adds {Math.max(2, Math.min(12, repeatCount))} sessions, {repeat === 'weekly' ? '7' : '14'} days apart.</p>
                  )}
                </div>
                <p style={builderHelpStyle}>Your local time — buyers see it converted to their own timezone. Add as many dates as you run this class.</p>
                {errors.eventDates && <p data-builder-error style={builderErrorStyle}>{errors.eventDates}</p>}
                {/* For a real event with no dates yet. Without this the only way to
                    get a listing live is to invent a date, which is what happened. */}
                <div className="flex items-start justify-between gap-3" style={{ marginTop: 12, padding: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-hover)' }}>
                  <div style={{ minWidth: 0 }}>
                    <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Dates to be announced</Label>
                    <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)', lineHeight: 1.45 }}>
                      Publish this without dates. The page says the dates are coming and people can
                      ask to be told when they are, instead of registering for a date that isn&rsquo;t real.
                      Adding a future date turns this off by itself.
                      {datesTBA && eventDates.some(isFutureLocal)
                        ? <><br /><strong>Remove the dates above for this to take effect.</strong></> : null}
                      {dateWatchers ? <><br /><strong>{dateWatchers} {dateWatchers === 1 ? 'person is' : 'people are'} waiting to hear.</strong></> : null}
                    </p>
                  </div>
                  {/* Deliberately NOT disabled while a date exists. Disabling it meant
                      the only route from "a date I made up" to "no dates yet" was to
                      delete the date FIRST, and nothing on screen said so — which is
                      precisely the state Nick's club was stuck in. Toggle in any
                      order; a real future date still wins, here and on the server. */}
                  <Switch checked={datesTBA} onCheckedChange={setDatesTBA} />
                </div>
              </div>
              <div>
                <Label style={builderLabelStyle}>Duration (minutes)</Label>
                <Input type="number" min="1" value={duration} onChange={e => setDuration(e.target.value)} placeholder="60" />
              </div>
              {/* One host block: name, email, title. The email used to have nowhere to
                  live, so every guest presenter was invisible to the room and to the
                  host reminder. */}
              <div className="flex flex-col gap-3" style={{ padding: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-hover)' }}>
                <div>
                  <Label style={builderLabelStyle}>Host name {eventDates.length > 1 && '(optional)'}</Label>
                  <Input value={hostName} onChange={e => setHostName(e.target.value)} placeholder="e.g. Francene Selkirk" />
                  <p style={builderHelpStyle}>
                    {eventDates.length > 1
                      ? 'Whoever runs the room every week. Leave blank if the host rotates and name them per date below.'
                      : 'Shown on the page and in the confirmation, reminders and calendar invite. Credits are fine here.'}
                  </p>
                </div>
                <div>
                  <Label style={builderLabelStyle}>Host email</Label>
                  <Input value={hostEmails} onChange={e => setHostEmails(e.target.value)} placeholder="e.g. ronen@example.com" />
                  <p style={builderHelpStyle}>
                    Puts them on camera instead of in the audience, and sends them the one-hour reminder. Commas for a panel. Never shown publicly.
                  </p>
                  {hostName.trim() && !hostEmails.trim() && (
                    <p className="m-0" style={{ ...builderHelpStyle, color: 'hsl(var(--destructive))', fontWeight: 600 }}>
                      Without this, {hostName.trim()} joins as audience and gets no reminder.
                    </p>
                  )}
                  {/* Saved addresses only — the check runs server-side against what is
                      stored, so it refreshes on load and after each save. */}
                  {hostCheck.map(h => (
                    <div key={h.email} style={{ marginTop: 8 }}>
                      <p className="m-0" style={{ fontSize: 11.5, color: h.hasAccount === false ? 'hsl(var(--destructive))' : 'var(--hj-beige-ink-2)', fontWeight: 600 }}>
                        {h.hasAccount === true
                          ? `✓ ${h.email} has an account${h.userType ? ` (${h.userType.replace(/_/g, ' ')})` : ''}`
                          : h.hasAccount === false
                            ? `${h.email} has no Hey Joe account, so they cannot be signed in and will join as audience.`
                            : `${h.email} — account status unavailable`}
                      </p>
                      {h.hasAccount === false && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(castingRequest(h.email));
                            toast({ title: 'Request copied', description: 'Paste it to whoever sets up casting accounts.' });
                          }}
                          className="inline-flex items-center gap-1.5"
                          style={{ marginTop: 5, padding: '6px 11px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
                        >
                          <Copy className="w-3 h-3" /> Copy account request for casting
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <Label style={builderLabelStyle}>Host title (optional)</Label>
                  <Input value={hostRole} onChange={e => setHostRole(e.target.value)} placeholder="Host" />
                  <p style={builderHelpStyle}>Defaults to &ldquo;Host&rdquo;. Use &ldquo;Instructor&rdquo;, &ldquo;Presenter&rdquo; or similar if it reads better.</p>
                </div>
              </div>

              {/* Co-owner stores. The event lists on each of their stores and they can
                  run it; the money split is the "Who gets paid" card under this. */}
              <div className="flex flex-col gap-3" style={{ padding: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-hover)' }}>
                <div>
                  <Label style={builderLabelStyle}>Co-owner stores</Label>
                  <p style={builderHelpStyle}>
                    Other stores that own this event with you. It shows on their store as well as yours, and they can edit it and see who registered. When you save, each new store gets an even share of the payout (50/50 with one partner), which you can change under Who gets paid.
                  </p>
                </div>
                {coOwners.map(c => (
                  <div key={c.slug} className="flex items-center justify-between gap-2" style={{ padding: '8px 10px', borderRadius: 8, background: '#fff', border: '1px solid var(--hj-beige-line)' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>
                      {c.displayName} <span style={{ fontWeight: 400, color: 'var(--hj-beige-ink-3)' }}>/creators/{c.slug}</span>
                    </span>
                    {canSetPayouts && (
                      <button type="button" onClick={() => { setCoOwners(cs => cs.filter(x => x.slug !== c.slug)); setDirty(true); }} aria-label="Remove store" title="Remove" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                {canSetPayouts ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={coOwnerInput}
                      onChange={e => setCoOwnerInput(e.target.value)}
                      placeholder="Store link or name, e.g. plus.heyjoe.io/creators/zak-barnett-studios"
                    />
                    <button
                      type="button"
                      disabled={coOwnerBusy || !coOwnerInput.trim() || coOwners.length >= 4}
                      onClick={async () => {
                        const raw = coOwnerInput.trim();
                        const slug = (raw.match(/creators\/([^/?#\s]+)/)?.[1] || raw).trim().toLowerCase();
                        if (coOwners.some(c => c.slug === slug)) { setCoOwnerInput(''); return; }
                        setCoOwnerBusy(true);
                        try {
                          const r = await fetch(`/api/creators/${encodeURIComponent(slug)}`);
                          const d = r.ok ? await r.json() : null;
                          if (!d?.page?.slug) throw new Error(`No store at /creators/${slug}`);
                          setCoOwners(cs => [...cs, { slug: d.page.slug, displayName: d.page.displayName || d.page.slug }]);
                          setCoOwnerInput('');
                          setDirty(true);
                        } catch (err) {
                          toast({ title: 'Store not found', description: err instanceof Error ? err.message : String(err), variant: 'destructive' });
                        } finally {
                          setCoOwnerBusy(false);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50"
                      style={{ padding: '8px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {coOwnerBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add store
                    </button>
                  </div>
                ) : (
                  <p className="m-0" style={builderHelpStyle}>Only the store that made this event, or a Hey Joe admin, can change its co-owners or the payout split.</p>
                )}
              </div>

              {/* Who gets paid. Shares of the creator payout, not of the ticket price;
                  anything unassigned stays with whoever owns the product. */}
              <div className="flex flex-col gap-3" style={{ padding: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-hover)' }}>
                <div>
                  <Label style={builderLabelStyle}>Who gets paid</Label>
                  <p style={builderHelpStyle}>
                    Split the creator payout with the people running this event. Percentages are of the payout, not the ticket price, and whatever you don&rsquo;t assign stays with you. Each person is paid to the Stripe account on their Hey Joe login; without one, their share waits until they connect at plus.heyjoe.io/reader/payouts.
                  </p>
                </div>
                {payees.map((x, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-1 flex flex-col gap-2">
                      <Input value={x.email} onChange={e => setPayees(ps => ps.map((p, j) => j === i ? { ...p, email: e.target.value } : p))} placeholder="email@example.com" />
                      <Input value={x.name} onChange={e => setPayees(ps => ps.map((p, j) => j === i ? { ...p, name: e.target.value } : p))} placeholder="Name (optional)" />
                    </div>
                    <div className="relative" style={{ width: 92, flexShrink: 0 }}>
                      <Input type="number" min="0" max="100" step="1" value={x.percent} onChange={e => setPayees(ps => ps.map((p, j) => j === i ? { ...p, percent: e.target.value } : p))} placeholder="50" className="pr-7" />
                      <span className="absolute" style={{ right: 10, top: 10, fontSize: 13, color: 'var(--hj-beige-ink-3)' }}>%</span>
                    </div>
                    <button type="button" onClick={() => setPayees(ps => ps.filter((_, j) => j !== i))} aria-label="Remove payee" title="Remove" className="flex-shrink-0" style={{ marginTop: 8, background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {(() => {
                  const sum = payees.reduce((a, x) => a + (parseFloat(x.percent) || 0), 0);
                  return sum > 100 ? (
                    <p className="m-0" style={{ ...builderHelpStyle, color: 'hsl(var(--destructive))', fontWeight: 600 }}>Shares add up to {sum}%. The most you can assign is 100%.</p>
                  ) : payees.length ? (
                    <p className="m-0" style={builderHelpStyle}>{sum}% assigned, {Math.max(0, 100 - sum)}% stays with you.</p>
                  ) : null;
                })()}
                <button
                  type="button"
                  onClick={() => setPayees(ps => [...ps, { email: '', name: '', percent: '' }])}
                  className="inline-flex items-center gap-1.5 self-start"
                  style={{ padding: '6px 11px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
                >
                  <Plus className="w-3 h-3" /> Add someone
                </button>
              </div>

              {eventDates.length > 1 && (
                <div>
                  {/* A show with a standing host has GUESTS on top of them. A show with
                      no standing host (the Waiting Room) rotates the HOST itself, and
                      calling that person a guest is just wrong. Same field either way —
                      the label follows whether a Host is set, so the screen tells the
                      truth about which kind of show this is. */}
                  <Label style={builderLabelStyle}>
                    {hostName.trim() ? 'Guest per date (optional)' : 'Host for each date'}
                  </Label>
                  <p style={builderHelpStyle}>
                    {hostName.trim() ? (
                      <>
                        Who&rsquo;s joining {hostName.trim()} on a particular date &mdash; add a row
                        each if there are two of them. They lead the event page with their
                        headshots, and they&rsquo;re named <em>alongside</em> the host in the
                        confirmation, the reminders and the calendar invite. Leave a date empty for
                        a week with no guest. Their email is what actually lets them into the room,
                        and only on their own date &mdash; a name on its own is billing, not access.
                      </>
                    ) : (
                      <>
                        This show rotates hosts, so name whoever is running each date &mdash; a row
                        each if there are two of them. They lead the event page with their
                        headshots, and they carry that date&rsquo;s confirmation, reminders and
                        calendar invite. Set a Host above instead if the same person runs every
                        week. Their email is what actually lets them into the room, and only on
                        their own date &mdash; a name on its own is billing, not access.
                      </>
                    )}
                  </p>
                  <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                    {sortDates(eventDates).map(d => {
                      const key = new Date(d).toISOString();
                      const rows = occHosts[key] || [];
                      const setRows = (next: HostDraft[]) => setOccHosts(m => ({ ...m, [key]: next }));
                      const setRow = (i: number, patch: Partial<HostDraft>) =>
                        setRows(rows.map((h, hi) => (hi === i ? { ...h, ...patch } : h)));
                      return (
                        <div key={key} style={{ border: '1px solid var(--hj-beige-line)', borderRadius: 10, padding: 12 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-beige-ink)', marginBottom: 8 }}>
                            {new Date(d).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                          </div>
                          {rows.length === 0 && (
                            <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)', marginBottom: 8 }}>
                              {hostName.trim() ? 'No guest this date.' : 'Nobody named yet — the page falls back to the Host above.'}
                            </p>
                          )}
                          <div style={{ display: 'grid', gap: 10 }}>
                            {rows.map((h, i) => {
                              const src = h.image
                                ? (h.image.startsWith('http') || h.image.startsWith('/api/') ? h.image : `/api/s3/${h.image}`)
                                : '';
                              const busy = uploadingHostPhoto === `${key}:${i}`;
                              return (
                                <div key={i} className="flex items-start gap-3">
                                  {/* The headshot is the whole point of the redesign, so it
                                      is the first thing in the row rather than an optional
                                      extra tucked behind a disclosure. */}
                                  <label className="cursor-pointer flex-shrink-0" title="Upload a headshot">
                                    {src ? (
                                      <img src={src} alt="" className="hj-headshot" style={{ width: 56, height: 56, objectFit: 'cover' }} />
                                    ) : (
                                      <span
                                        className="flex items-center justify-center hj-headshot"
                                        style={{ width: 56, height: 56, background: 'var(--hj-beige-hover)', border: '1px dashed var(--hj-beige-line)', color: 'var(--hj-beige-ink-3)' }}
                                      >
                                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                                      </span>
                                    )}
                                    <input type="file" accept="image/*" className="hidden" disabled={!!uploadingHostPhoto} onChange={e => handleHostPhotoUpload(key, i, e)} />
                                  </label>
                                  <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 6 }}>
                                    <div className="hj-occ-pair">
                                      <Input value={h.name} onChange={e => setRow(i, { name: e.target.value })} placeholder={hostName.trim() ? 'Guest name' : 'Host name'} />
                                      <Input value={h.role} onChange={e => setRow(i, { role: e.target.value })} placeholder={hostName.trim() ? 'Guest' : 'Host'} />
                                    </div>
                                    <Input value={h.email} onChange={e => setRow(i, { email: e.target.value })} placeholder="Their email — gets them into the room that week" />
                                    <div className="flex items-center gap-3">
                                      <button type="button" onClick={() => setRows(rows.filter((_, hi) => hi !== i))} style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>Remove</button>
                                      {h.image && (
                                        <button type="button" onClick={() => setRow(i, { image: '' })} style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>Remove headshot</button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={() => setRows([...rows, emptyHost()])}
                            className="inline-flex items-center gap-1.5"
                            style={{ marginTop: rows.length ? 10 : 0, padding: '6px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            <Plus className="w-3.5 h-3.5" /> {rows.length ? 'Add another' : (hostName.trim() ? 'Add a guest' : 'Add a host')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* What's different about ONE date. A recurring event can change venue and
                  subject week to week — the product description only covers what the
                  thing IS, not what's on this time. Kept out of the way of a plain
                  one-off webinar, which needs none of it. */}
              {!isGameShow && eventDates.length > 0 && (eventDates.length > 1 || hasVenue) && (
                <div>
                  <Label style={builderLabelStyle}>Details for each date (optional)</Label>
                  <p style={builderHelpStyle}>
                    What&rsquo;s on that particular time, in your own words. It shows on the event
                    page and goes into that date&rsquo;s confirmation and reminder emails.
                    {hasVenue && ' Leave the venue blank to use the address above.'}
                  </p>
                  <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                    {sortDates(eventDates).map(d => {
                      const key = new Date(d).toISOString();
                      const v = occDetails[key] || emptyVenue();
                      const set = (patch: Partial<typeof v>) =>
                        setOccDetails(m => ({ ...m, [key]: { ...v, ...patch } }));
                      return (
                        <div key={key} style={{ border: '1px solid var(--hj-beige-line)', borderRadius: 10, padding: 12 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-beige-ink)', marginBottom: 8 }}>
                            {new Date(d).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                          </div>
                          {hasVenue && (
                            <div className="hj-occ-pair" style={{ marginBottom: 8 }}>
                              <Input value={v.name} onChange={e => set({ name: e.target.value })} placeholder={venue.name.trim() ? `Venue (default: ${venue.name.trim()})` : 'Venue for this date'} />
                              <Input value={v.address} onChange={e => set({ address: e.target.value })} placeholder="Street address for this date" />
                            </div>
                          )}
                          <Textarea
                            value={v.details}
                            onChange={e => set({ details: e.target.value })}
                            rows={4}
                            placeholder={"What's happening this time, and anything attendees should know. Leave a blank line between paragraphs."}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* A game show streams in-platform, so there is no external room link. */}
              {!isGameShow && (
                <div>
                  <Label style={builderLabelStyle}>Where it happens</Label>
                  <p style={builderHelpStyle}>
                    An in-person event asks for an address instead of a link. Hybrid asks for both.
                  </p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    {([
                      { key: 'online', label: 'Online' },
                      { key: 'in_person', label: 'In person' },
                      { key: 'both', label: 'Both' },
                    ] as const).map(opt => {
                      const on = locationMode === opt.key;
                      return (
                        <button key={opt.key} type="button"
                          onClick={() => { setLocationMode(opt.key); setErrors(x => ({ ...x, joinUrl: '', venueAddress: '' })); }}
                          style={{ padding: '8px 16px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 700, background: on ? 'var(--hj-beige-ink)' : 'transparent', color: on ? 'var(--hj-beige)' : 'var(--hj-beige-ink)', border: `1.5px solid ${on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)'}` }}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {hasVenue && (
                    <div style={{ display: 'grid', gap: 8, marginBottom: hasOnline ? 16 : 0 }}>
                      <div className="hj-occ-pair">
                        <div>
                          <Input value={venue.name} onChange={e => setVenue(v => ({ ...v, name: e.target.value }))} placeholder="Venue name" />
                          <p style={builderHelpStyle}>Shown publicly. It&rsquo;s how the event sells itself.</p>
                        </div>
                        <div>
                          <Input value={venue.city} onChange={e => setVenue(v => ({ ...v, city: e.target.value }))} placeholder="City, State" />
                          <p style={builderHelpStyle}>Shown publicly.</p>
                        </div>
                      </div>
                      <div>
                        <Input value={venue.address} onChange={e => { setVenue(v => ({ ...v, address: e.target.value })); setErrors(x => ({ ...x, venueAddress: '' })); }} placeholder="Street address" />
                        <p style={builderHelpStyle}>
                          Only registered attendees see this, the way a join link is only shown to
                          them. It goes in the confirmation, the reminders and the calendar invite,
                          where a phone turns it into directions.
                        </p>
                        {errors.venueAddress && <p data-builder-error style={builderErrorStyle}>{errors.venueAddress}</p>}
                      </div>
                      <div>
                        <Input value={venue.notes} onChange={e => setVenue(v => ({ ...v, notes: e.target.value }))} placeholder="Where to meet once you're there (optional)" />
                        <p style={builderHelpStyle}>How to find everyone once they&rsquo;re there. Registered attendees only.</p>
                      </div>
                    </div>
                  )}
                  {hasOnline && (
                  <>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {([
                      { key: 'heyjoe', title: 'Hey Joe event room', body: 'Made for you when you save. Presenters go on camera, everyone else watches the stream, and registered buyers get the link automatically.' },
                      { key: 'own', title: 'My own link', body: 'Zoom, Meet, or anywhere else. Only revealed to registered buyers.' },
                    ] as const).filter(opt => opt.key === 'own' || EVENT_ROOMS_AVAILABLE || !!eventRoom?.hostUrl || roomMode === 'heyjoe').map(opt => {
                      const on = roomMode === opt.key;
                      return (
                        <button key={opt.key} type="button" onClick={() => { setRoomMode(opt.key); setErrors(x => ({ ...x, joinUrl: '' })); }}
                          style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', background: on ? 'var(--hj-beige-hover, rgba(0,0,0,0.04))' : 'transparent', border: `1.5px solid ${on ? 'var(--hj-beige-ink)' : 'var(--hj-beige-line)'}`, color: 'var(--hj-beige-ink)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700 }}>
                            <span style={{ width: 14, height: 14, borderRadius: 999, border: '1.5px solid currentColor', display: 'inline-block', background: on ? 'currentColor' : 'transparent', boxShadow: on ? 'inset 0 0 0 3px var(--hj-bg, #fff)' : 'none' }} />
                            {opt.title}
                          </div>
                          <div style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5, marginTop: 2 }}>{opt.body}</div>
                        </button>
                      );
                    })}
                  </div>
                  {roomMode === 'heyjoe' && !isGameShow && (
                    <div className="flex items-start justify-between gap-3" style={{ marginTop: 10, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: '#fff', border: '1px solid var(--hj-beige-line)' }}>
                      <div>
                        <p className="m-0" style={{ fontSize: 13, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Everyone joins the meeting</p>
                        <p style={{ ...builderHelpStyle, marginTop: 2 }}>
                          {audienceMode === 'meeting'
                            ? 'Attendees land in the room with you, on camera if they want, like the Waiting Room. The room holds 150, so registration closes there unless you set a lower cap.'
                            : 'Off: presenters are in the meeting and everyone else watches the stream, which has no ceiling. Turn on for a class where people should be in the room.'}
                        </p>
                      </div>
                      <Switch checked={audienceMode === 'meeting'} onCheckedChange={v => setAudienceMode(v ? 'meeting' : 'stream')} />
                    </div>
                  )}
                  {roomMode === 'heyjoe' && (
                    joinUrl ? (
                      <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-beige-ink)', lineHeight: 1.6 }}>
                        <div>Room: <a href={joinUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600 }}>{joinUrl}</a></div>
                        {eventRoom?.hostUrl && <div>Host controls: <a href={eventRoom.hostUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600 }}>{eventRoom.hostUrl}</a></div>}
                        <div style={{ color: 'var(--hj-beige-ink-2)' }}>Title and dates re-sync to the room every time you save.</div>
                      </div>
                    ) : (
                      <p style={builderHelpStyle}>{isEditing ? 'Save to create the room.' : 'The room is created when you save.'}</p>
                    )
                  )}
                  {roomMode === 'own' && (
                    <div style={{ marginTop: 8 }}>
                      <Input value={joinUrl} onChange={e => { setJoinUrl(e.target.value); setErrors(x => ({ ...x, joinUrl: '' })); }} placeholder="https://… the room/stream link" />
                      {errors.joinUrl && <p data-builder-error style={builderErrorStyle}>{errors.joinUrl}</p>}
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Recording included</Label>
                  <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>Registrants are promised the recording — it becomes part of what you're delivering.</p>
                </div>
                <Switch checked={recordingIncluded} onCheckedChange={setRecordingIncluded} />
              </div>
              {recordingIncluded && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--hj-beige-line)' }}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Active replay</Label>
                      <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>
                        You promise to answer every question asked under the replay within 24 hours, for a set number of days after the last session. People who can't make it live get a reason to register anyway.
                      </p>
                    </div>
                    <Switch checked={activeReplayOn} onCheckedChange={setActiveReplayOn} />
                  </div>
                  {activeReplayOn && (
                    <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}>Answer questions for</span>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={activeReplayDays}
                        onChange={e => setActiveReplayDays(e.target.value)}
                        style={{ width: 72 }}
                        aria-label="Active replay days"
                      />
                      <span style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}>days after the last session</span>
                    </div>
                  )}
                </div>
              )}
            </BuilderCard>

            <BuilderCard kicker="Pricing">
              <div>
                <Label style={builderLabelStyle}>Price (USD)</Label>
                <Input type="number" min="0" step="0.01" value={priceInput} onChange={e => { setPriceInput(e.target.value); setErrors(x => ({ ...x, price: '' })); }} placeholder="15.00" />
                <p style={builderHelpStyle}>Enter $0 to make it free, or at least $1.00.</p>
                {errors.price && <p data-builder-error style={builderErrorStyle}>{errors.price}</p>}
              </div>

              {/* Ticket types — e.g. "Work with me" (limited participant seats)
                  vs "Observer". Optional; without them the single price applies. */}
              <div style={{ marginTop: 18 }}>
                <div className="flex items-center justify-between">
                  <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Ticket types (optional)</Label>
                  {tierDrafts.length < 4 && (
                    <button
                      type="button"
                      onClick={() => setTierDrafts(ts => [...ts, { label: '', price: '', seatCap: '', description: '' }])}
                      style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hj-deep-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      + Add ticket type
                    </button>
                  )}
                </div>
                <p style={builderHelpStyle}>
                  Sell different seats at different prices — like "Work with me" with limited spots and a cheaper "Observer" seat. Buyers pick one at checkout; seat caps sell out automatically.
                </p>
                {tierDrafts.map((t, i) => (
                  <div key={i} style={{ marginTop: 10, padding: 12, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-hover)' }}>
                    <div className="flex items-center gap-2">
                      <Input
                        value={t.label}
                        onChange={e => { setTierDrafts(ts => ts.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x)); setErrors(x => ({ ...x, tiers: '' })); }}
                        placeholder={i === 0 ? 'Work with me' : 'Observer'}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        aria-label="Remove ticket type"
                        onClick={() => setTierDrafts(ts => ts.filter((_, xi) => xi !== i))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hj-beige-ink-2)', padding: 4 }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2" style={{ marginTop: 8 }}>
                      <div style={{ flex: 1 }}>
                        <Input
                          type="number" min="0" step="0.01"
                          value={t.price}
                          onChange={e => { setTierDrafts(ts => ts.map((x, xi) => xi === i ? { ...x, price: e.target.value } : x)); setErrors(x => ({ ...x, tiers: '' })); }}
                          placeholder="Price (USD)"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Input
                          type="number" min="1" step="1"
                          value={t.seatCap}
                          onChange={e => { setTierDrafts(ts => ts.map((x, xi) => xi === i ? { ...x, seatCap: e.target.value } : x)); setErrors(x => ({ ...x, tiers: '' })); }}
                          placeholder="Seat cap (blank = unlimited)"
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Input
                        value={t.description}
                        onChange={e => setTierDrafts(ts => ts.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x))}
                        placeholder="Short description, e.g. Perform in the session and get direct feedback"
                      />
                    </div>
                  </div>
                ))}
                {errors.tiers && <p data-builder-error style={builderErrorStyle}>{errors.tiers}</p>}
              </div>
            </BuilderCard>

            {/* Registration roster — who's coming. Edit mode only. */}
            {!isGameShow && (
              <BuilderCard kicker="After the event">
                <div className="flex items-start justify-between gap-3" style={{ marginBottom: followupOn ? 14 : 0 }}>
                  <div>
                    <Label style={builderLabelStyle}>Follow-up emails</Label>
                    <p style={builderHelpStyle}>
                      {roomMode === 'heyjoe'
                        ? 'An hour after the stream ends, people who came get a thank-you and people who didn\u2019t get a "we missed you", both with the replay and your next date. Hey Joe works out who was in the room. Leave a field blank to use our wording.'
                        : 'An hour after the event ends, everyone registered gets a thank-you with your next date, and the replay if one is posted. Paste your attendee list on the roster after the event and the two emails split into attended and missed.'}
                    </p>
                  </div>
                  <Switch checked={followupOn} onCheckedChange={setFollowupOn} />
                </div>
                {followupOn && (
                  <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    {([['attended', 'To people who came', followupAttended, setFollowupAttended, 'Thanks for coming to {title}'],
                       ['missed', 'To people who didn\u2019t make it', followupMissed, setFollowupMissed, 'We missed you at {title}. The replay is ready.']] as const).map(([key, label, val, set, ph]) => (
                      <div key={key}>
                        <Label style={builderLabelStyle}>{label}</Label>
                        <Input value={val.subject} onChange={e => set({ ...val, subject: e.target.value })} placeholder={ph} style={{ marginBottom: 6 }} />
                        <Textarea value={val.body} onChange={e => set({ ...val, body: e.target.value })} rows={6}
                          placeholder={key === 'attended'
                            ? 'Hi {name},\n\nThanks for being in the room for {title}. The replay is up.\n\n{next}'
                            : 'Hi {name},\n\nWe missed you at {title}. The full recording is ready now.\n\n{next}'} />
                        <p style={builderHelpStyle}>{'{name}'}, {'{title}'} and {'{next}'} fill themselves in. The replay button is always added.</p>
                      </div>
                    ))}
                  </div>
                )}
              </BuilderCard>
            )}
            {isEditing && <RosterCard productId={productId} kicker="Roster" noun="registered" fileLabel="roster" title={title} />}
          </div>

          {/* Right: rail */}
          <div className="w-full lg:w-[308px] lg:flex-shrink-0 lg:sticky flex flex-col gap-5" style={{ top: 88 }}>
            <BuilderCard kicker="Cover image">
              <div
                className="w-full flex items-center justify-center overflow-hidden"
                style={{ aspectRatio: '16 / 10', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-hover)', border: '1px dashed var(--hj-beige-line)' }}
              >
                {coverSrc ? (
                  <img src={coverSrc} alt="Cover" className="w-full h-full" style={{ objectFit: 'cover' }} />
                ) : (
                  <div className="text-center" style={{ color: 'var(--hj-beige-ink-3)' }}>
                    <ImageIcon className="w-6 h-6 mx-auto" />
                    <div style={{ fontSize: 11.5, marginTop: 4 }}>Upload cover</div>
                  </div>
                )}
              </div>
              <label className="cursor-pointer">
                <span
                  className="inline-flex items-center gap-1.5 w-full justify-center"
                  style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38 }}
                >
                  {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {coverImage ? 'Change cover' : 'Upload cover'}
                </span>
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploadingCover} />
              </label>
              <MakeInStudioLink output="card" eyebrow={isGameShow ? 'LIVE GAME SHOW' : 'FREE LIVE WEBINAR'} headline={title || undefined} coverProductId={isEditing ? productId : undefined} />
              {coverImage && (
                <button type="button" onClick={() => setCoverImage('')} className="self-start" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer' }}>Remove</button>
              )}
            </BuilderCard>

            <BuilderCard>
              <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Downloads for registrants</Label>
              <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)', marginBottom: 10 }}>Slides, a worksheet, a PDF. Registrants see them on the event page and on the replay.</p>
              {downloads.length > 0 && (
                <ul className="m-0 p-0" style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  {downloads.map((d) => (
                    <li key={d.id} className="flex items-center gap-2">
                      <input type="text" value={d.title} aria-label="Download title" onChange={(e) => setDownloads((list) => list.map((x) => (x.id === d.id ? { ...x, title: e.target.value } : x)))}
                        style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--hj-beige-line)', fontSize: 13, fontFamily: 'inherit', background: '#fff', color: 'var(--hj-beige-ink)' }} />
                      <span style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', flexShrink: 0 }}>{d.bytes ? `${(d.bytes / 1048576).toFixed(1)} MB` : ''}</span>
                      <button type="button" aria-label={`Remove ${d.title}`} onClick={() => setDownloads((list) => list.filter((x) => x.id !== d.id))} style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)', padding: 4 }}><Trash2 className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
              <label className="cursor-pointer">
                <span className="inline-flex items-center gap-1.5" style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 36, opacity: downloads.length >= 10 ? 0.5 : 1 }}>
                  {uploadingDownload ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {downloads.length ? 'Add another file' : 'Add a file'}
                </span>
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleDownloadUpload} disabled={uploadingDownload || downloads.length >= 10} />
              </label>
            </BuilderCard>

            <BuilderCard>
              <div className="flex items-center justify-between">
                <div>
                  <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Published</Label>
                  <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>Visible in your store.</p>
                </div>
                <Switch checked={published} onCheckedChange={setPublished} />
              </div>
            </BuilderCard>

            {/* Questions at registration — creator-defined, answered after registering
                (and with early-access requests on agency-only events). */}
            {!isGameShow && (
              <BuilderCard kicker="Questions at registration">
                <p className="m-0" style={builderHelpStyle}>
                  Ask registrants a few things after they sign up. On an agency-only event, people outside the roster answer the same questions when they ask for early access. Answers show on your roster and in the CSV.
                </p>
                {questions.length > 0 && (
                  <ol className="m-0 p-0 flex flex-col gap-2.5" style={{ listStyle: 'none' }}>
                    {questions.map((q, i) => {
                      const meta = Q_TYPES.find(t => t.value === q.type) || Q_TYPES[0];
                      return (
                        <li key={q.id} className="flex flex-col gap-2" style={{ padding: 10, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-beige-hover)' }}>
                          <div className="flex items-center gap-2">
                            <span className="mono-label flex-shrink-0" style={{ color: 'var(--hj-beige-ink-2)', minWidth: 18 }}>{i + 1}</span>
                            <input
                              value={q.label}
                              onChange={e => patchQuestion(i, { label: e.target.value })}
                              placeholder="The question"
                              aria-label={`Question ${i + 1}`}
                              style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600 }}
                            />
                            <button type="button" onClick={() => removeQuestion(i)} aria-label="Remove question" title="Remove" className="flex-shrink-0" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <select
                              value={q.type}
                              onChange={e => { const t = e.target.value as RegistrationQuestionType; patchQuestion(i, { type: t, options: Q_TYPES.find(x => x.value === t)?.hasOptions ? (q.options?.length ? q.options : ['']) : undefined }); }}
                              aria-label="Answer type"
                              style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12 }}
                            >
                              {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <label className="inline-flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--hj-beige-ink)' }}>
                              <input type="checkbox" checked={!!q.required} onChange={e => patchQuestion(i, { required: e.target.checked })} /> Required
                            </label>
                          </div>
                          {meta.hasOptions && (
                            <div className="flex flex-col gap-1.5" style={{ paddingLeft: 26 }}>
                              {(q.options || []).map((o, oi) => (
                                <div key={oi} className="flex items-center gap-2">
                                  <input
                                    value={o}
                                    onChange={e => { const next = (q.options || []).slice(); next[oi] = e.target.value; patchQuestion(i, { options: next }); }}
                                    placeholder={`Choice ${oi + 1}`}
                                    aria-label={`Question ${i + 1} choice ${oi + 1}`}
                                    style={{ flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5 }}
                                  />
                                  <button type="button" onClick={() => patchQuestion(i, { options: (q.options || []).filter((_, j) => j !== oi) })} aria-label="Remove choice" disabled={(q.options || []).length <= 1} style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)', opacity: (q.options || []).length <= 1 ? 0.35 : 1 }}><X className="w-3.5 h-3.5" /></button>
                                </div>
                              ))}
                              {(q.options || []).length < 10 && (
                                <button type="button" onClick={() => patchQuestion(i, { options: [...(q.options || []), ''] })} className="self-start" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--hj-beige-ink)', textDecoration: 'underline' }}>Add a choice</button>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
                {questions.length < 8 && (
                  <button type="button" onClick={addQuestion} className="inline-flex items-center gap-1.5 w-full justify-center" style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}>
                    {questions.length ? 'Add another question' : 'Add a question'}
                  </button>
                )}
              </BuilderCard>
            )}

            {/* Replay chapters — generated from the recording once the event has run. */}
            {isEditing && !isGameShow && (
              <BuilderCard kicker="Replay chapters">
                {replayChapters ? (
                  <div className="flex flex-col gap-2.5">
                    <p className="m-0" style={builderHelpStyle}>
                      Buyers see these on the replay's scrub bar and as a clickable list. Retitle anything, or regenerate from the recording.
                    </p>
                    <textarea
                      value={chapterOverview}
                      onChange={e => { setChapterOverview(e.target.value); setChaptersDirty(true); }}
                      rows={2}
                      placeholder="Two sentences on what the replay covers"
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5, lineHeight: 1.5, resize: 'vertical' }}
                    />
                    <ol className="m-0 p-0 flex flex-col gap-1.5" style={{ listStyle: 'none' }}>
                      {chapterDraft.map((c, i) => (
                        <li key={`${c.startSec}-${i}`} className="flex items-center gap-2">
                          <span className="mono-label flex-shrink-0" style={{ minWidth: 52, color: 'var(--hj-beige-ink-2)', fontVariantNumeric: 'tabular-nums' }}>{formatClock(c.startSec)}</span>
                          <input
                            value={c.title}
                            onChange={e => { const next = chapterDraft.slice(); next[i] = { ...c, title: e.target.value }; setChapterDraft(next); setChaptersDirty(true); }}
                            aria-label={`Chapter ${i + 1} title`}
                            style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600 }}
                          />
                          <button type="button" onClick={() => { setChapterDraft(chapterDraft.filter((_, j) => j !== i)); setChaptersDirty(true); }} aria-label="Remove chapter" title="Remove" disabled={chapterDraft.length <= 1} className="flex-shrink-0" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))', opacity: chapterDraft.length <= 1 ? 0.35 : 1 }}><Trash2 className="w-4 h-4" /></button>
                        </li>
                      ))}
                    </ol>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={saveChapters} disabled={!chaptersDirty || !!chaptersBusy} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: chaptersDirty ? 'var(--hj-beige-ink)' : '#fff', border: '1px solid var(--hj-beige-line)', color: chaptersDirty ? '#fff' : 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, minHeight: 34, cursor: chaptersDirty ? 'pointer' : 'default', opacity: chaptersBusy ? 0.6 : 1 }}>
                        {chaptersBusy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save chapters
                      </button>
                      <button type="button" onClick={regenerateChapters} disabled={!!chaptersBusy} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12, fontWeight: 600, minHeight: 34, cursor: 'pointer' }}>
                        {chaptersBusy === 'generate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Regenerate from recording
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <p className="m-0" style={builderHelpStyle}>
                      Once the event has run and the recording is on the page, chapters are generated from it automatically (within about half an hour). Or start it now.
                    </p>
                    <button type="button" onClick={regenerateChapters} disabled={!!chaptersBusy} className="inline-flex items-center gap-1.5 w-full justify-center" style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}>
                      {chaptersBusy === 'generate' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Generate chapters
                    </button>
                  </div>
                )}
              </BuilderCard>
            )}

            {/* Game-show run of show — questions/rounds live in the host panel. */}
            {isGameShow && (
              <BuilderCard kicker="Game">
                <div className="font-hj-display" style={{ fontSize: 15, fontWeight: 700, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}>
                  Run of show
                </div>
                <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}>
                  Questions, rounds, and scoring are authored in the host panel. Build your show there, then run it live from the same screen — your audience plays along on their phones.
                </p>
                {gameShowId ? (
                  <>
                    <p className="m-0" style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', color: 'var(--hj-beige-ink)' }}>Next step</p>
                    <button
                      type="button"
                      onClick={() => navigate(`/show-host/${gameShowId}`)}
                      className="inline-flex items-center gap-2 w-full justify-center"
                      style={{ padding: '12px 16px', borderRadius: 999, background: 'var(--hj-pink)', border: '1px solid var(--hj-pink)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 800, minHeight: 46, cursor: 'pointer', boxShadow: '0 6px 18px rgb(224 74 168 / 0.28)' }}
                    >
                      <Gamepad2 className="w-4 h-4" /> Open host panel →
                    </button>
                  </>
                ) : (
                  <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)' }}>
                    Save this show to open the host panel.
                  </p>
                )}
              </BuilderCard>
            )}

            <BuilderCard kicker="What happens on register">
              <ul className="m-0 flex flex-col gap-3" style={{ listStyle: 'none', padding: 0 }}>
                <li className="flex items-start gap-2.5">
                  <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, background: 'rgb(65 113 249 / 0.12)', color: '#3a5fd0' }}><Mail className="w-3.5 h-3.5" /></span>
                  <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}>Buyer gets a confirmation email with the join link and a calendar invite.</p>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, background: 'rgb(140 198 63 / 0.16)', color: '#5a8a2e' }}><Users className="w-3.5 h-3.5" /></span>
                  <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}>You get a new-registration alert, and the roster shows up in your Sales.</p>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--hj-pink-tint)', color: '#b3438f' }}><CalendarDays className="w-3.5 h-3.5" /></span>
                  <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}>{`The ${isGameShow ? 'show' : 'webinar'} shows on your storefront and marketplace card as an upcoming event.`}</p>
                </li>
              </ul>
            </BuilderCard>
          </div>
        </div>
      </div>

      {/* Intro-video sources + editor (single page-level instances) */}
      <RecordingModal open={showRecordModal} onClose={() => setShowRecordModal(false)} onSave={handlePreviewRecorded} />
      <LibraryPicker open={showLibraryPicker} stageTarget="lesson" onClose={() => setShowLibraryPicker(false)} onPick={handlePreviewLibraryPick} />
      {editingPreview && !!previewEditKey && (
        <VideoEditDrawer
          open
          video={{ s3Key: previewEditKey, title: previewClipName || 'Intro video', muxPlaybackId: existingMuxPreview?.muxPlaybackId }}
          onApplied={applyPreviewEdit}
          onClose={() => setEditingPreview(false)}
        />
      )}
    </main>
  );
};

export default WebinarBuilderPage;
